import { Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import { HSpacer, RoutalDialog } from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useEffect, useState } from 'react';

import CrossIcon from '../../../assets/svg/CrossIcon';
import { CrossIconStyled } from '../../../dialogs/DialogCloseIcon';
import { Client } from '../../../domain/client/Client';
import { useCreateClients } from '../../../hooks/client/useCreateClients';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import Button from '../../../react-components/Button';
import InputLocation from '../../../react-components/InputLocation';
import InputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';
import eventsPanel from '../../../services/events';

interface ClientsFromScratchDialogProps {
  open: boolean;
  initialLocation?: Location;
  handleCloseDialog: (...args: any[]) => any;
}

type NewClientData = {
  label: string;
  location?: Location;
};

export const ClientsFromScratchDialog = ({
  open,
  initialLocation,
  handleCloseDialog,
}: ClientsFromScratchDialogProps) => {
  const { run: createClients } = useCreateClients();
  const { data: activeProject } = useRetrieveActiveProject();
  const [clients, setClients] = useState<NewClientData[]>([]);

  useEffect(() => {
    if (initialLocation) {
      setClients([{ label: ``, location: initialLocation }]);
    }
    return () => {
      setClients([]);
    };
  }, [initialLocation, open]);

  const handleCreateClients = (e: any) => {
    const cleanClients: Client[] = clients.map((client: NewClientData) => {
      const { label, location } = client;

      return Client.create({
        ...(label && label !== `` ? { label: label } : {}),
        project_id: activeProject!.id,
        location,
      });
    });
    createClients({ clients: cleanClients });
    eventsPanel.createClients(`From scratch`);
    handleCloseDialog(e);
  };

  return (
    <RoutalDialog
      open={open}
      dialogProps={{
        maxWidth: `sm`,
      }}
      onCloseDialog={handleCloseDialog}
      showCrossCloseButton={false}
      titleRightContent={
        <Grid item container justifyContent="flex-end" alignItems="center" wrap="nowrap">
          <Button
            style={{ minWidth: `120px` }}
            onClick={handleCreateClients}
            intlId={clients.length > 1 ? `create_number` : `create`}
            intlValues={clients.length > 1 ? { number: clients.length } : {}}
            disabled={clients.length === 0}
          />
          <CrossIconStyled onClick={handleCloseDialog} />
        </Grid>
      }
      titleContent={<Text variant="h6" intlId="clients.dialogs.clients_from_scratch.title" />}
      contentStyles={{
        height: `400px`,
      }}
      showFooter={false}
    >
      <Grid container direction="column" wrap="nowrap" style={{ position: `relative`, height: `400px` }}>
        {_.range(clients.length + 1).map((index: number) => {
          return (
            <Grid
              item
              container
              direction="row"
              wrap="nowrap"
              key={`client-${index}`}
              alignItems="center"
              style={{ marginBottom: `8px` }}
            >
              <Grid item container>
                <InputText
                  fullWidth
                  tabIndex={index * 2}
                  size="medium"
                  autoFocus={index === 0}
                  variant="outlined"
                  placeholderIntlId="clients.dialogs.clients_from_scratch.label.placeholder"
                  fieldValue={clients[index]?.label}
                  handleEnter={(val: string) => {
                    const newClients = [...clients];
                    if (index >= clients.length) {
                      newClients.push({ label: val });
                    } else {
                      newClients[index].label = val;
                    }
                    setClients(newClients);
                  }}
                />
              </Grid>

              <HSpacer large />

              <Grid item container>
                <InputLocation
                  fullWidth
                  inputProps={{
                    tabIndex: index * 2 + 1,
                    styles: {},
                    size: `medium`,
                    variant: `outlined`,
                    placeholderIntlId: `location`,
                  }}
                  emptyIntlId="no_results"
                  fieldValue={clients[index]?.location}
                  onUpdate={(location?: Location | null) => {
                    const newClients = [...clients];
                    if (index >= clients.length) {
                      newClients.push({ label: ``, location: location ?? undefined });
                    } else {
                      newClients[index].location = location ?? undefined;
                    }
                    setClients(newClients);
                  }}
                />
              </Grid>

              <Grid item style={{ width: `24px`, cursor: `pointer` }}>
                {index < clients.length ? (
                  <CrossIcon
                    style={{
                      color: `#D2190B`,
                      fontSize: `14px`,
                      marginLeft: `8px`,
                    }}
                    onClick={() => {
                      setClients(clients.filter((s: NewClientData, idx: number) => idx !== index));
                    }}
                  />
                ) : (
                  <div style={{ width: `22px` }} />
                )}
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </RoutalDialog>
  );
};
